import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Tabs, Tab, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Alert, Chip, Snackbar,
} from "@mui/material";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import api from "../../utils/api";
import MarinaDashboard from "./MarinaDashboard";
import MarinaDocksSlips from "./MarinaDocksSlips";
import MarinaReservations from "./MarinaReservations";
import MarinaVessels from "./MarinaVessels";
import MarinaWaitlist from "./MarinaWaitlist";
import MarinaMap   from "./MarinaMap";
import MarinaRates from "./MarinaRates";
import MarinaIncidents from "./MarinaIncidents";
import MarinaAdvancedSettings from "./MarinaAdvancedSettings";

/* ── Marina module shell ────────────────────────────────────────────────── */
const Marina = () => {
  const [marinas, setMarinas] = useState([]);
  const [selectedMarinaId, setSelectedMarinaId] = useState(null);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const fetchMarinas = useCallback(async () => {
    try {
      const res = await api.get("/marinas");
      const list = res.data || [];
      setMarinas(list);
      if (list.length > 0 && !selectedMarinaId) setSelectedMarinaId(list[0].id);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [selectedMarinaId]);

  useEffect(() => { fetchMarinas(); }, [fetchMarinas]);

  const selectedMarina = marinas.find((m) => m.id === selectedMarinaId);
  const showSnack = (message, severity = "success") => setSnack({ open: true, message, severity });

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: "flex", flexDirection: "column", minHeight: 0 }}>
      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: "14px", backgroundColor: "rgba(47,99,173,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <DirectionsBoatIcon sx={{ color: "#2f63ad", fontSize: 26 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#223142", lineHeight: 1.2 }}>
              Marina Management
            </Typography>
            {selectedMarina && (
              <Typography variant="body2" sx={{ color: "#6b778c" }}>
                {selectedMarina.name} · {selectedMarina.slip_count ?? "N/A"} slips · {selectedMarina.available_slips ?? "N/A"} available
              </Typography>
            )}
          </Box>
        </Box>

        {marinas.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 200, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}>
            <InputLabel>Marina</InputLabel>
            <Select value={selectedMarinaId ?? ""} label="Marina"
              onChange={(e) => { setSelectedMarinaId(e.target.value); setTab(0); }}>
              {marinas.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.name}
                  {m.available_slips != null && (
                    <Chip label={`${m.available_slips} avail.`} size="small"
                      sx={{ ml: 1, height: 18, fontSize: "0.7rem" }} />
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* ── Content ── */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>
      ) : marinas.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: "12px" }}>
          No marinas found. Go to <strong>System Preferences → Marina Settings</strong> to add one.
        </Alert>
      ) : (
        <>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ mb: 3, borderBottom: "1px solid #eef2f7", "& .MuiTab-root": { textTransform: "none", fontWeight: 600 } }}
          >
            <Tab label="Dashboard" />
            <Tab label="Docks & Slips" />
            <Tab label="Rates" />
            <Tab label="Marina Map" />
            <Tab label="Reservations" />
            <Tab label="Vessels & Owners" />
            <Tab label="Waitlist" />
            <Tab label="Incidents" />
            <Tab label="Settings" />
          </Tabs>

          {tab === 0 && <MarinaDashboard marinaId={selectedMarinaId} />}
          {tab === 1 && <MarinaDocksSlips marinaId={selectedMarinaId} showSnack={showSnack} />}
          {tab === 2 && <MarinaRates marinaId={selectedMarinaId} showSnack={showSnack} />}
          {tab === 3 && (
            <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
              <MarinaMap marinaId={selectedMarinaId} showSnack={showSnack} />
            </Box>
          )}
          {tab === 4 && <MarinaReservations marinaId={selectedMarinaId} showSnack={showSnack} />}
          {tab === 5 && <MarinaVessels marinaId={selectedMarinaId} showSnack={showSnack} />}
          {tab === 6 && <MarinaWaitlist marinaId={selectedMarinaId} showSnack={showSnack} />}
          {tab === 7 && <MarinaIncidents marinaId={selectedMarinaId} />}
          {tab === 8 && <MarinaAdvancedSettings marinaId={selectedMarinaId} />}
        </>
      )}

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack((p) => ({ ...p, open: false }))} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity={snack.severity} onClose={() => setSnack((p) => ({ ...p, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Marina;
